import React from 'react'
// libs
import styled from 'styled-components'
import { useDispatch, useSelector, batch } from 'react-redux'
import { change, FieldArray, formValueSelector, getFormMeta } from 'redux-form'
// utils
import { values, map, flatten, some, flow, times } from 'lodash/fp'
// components
import ResultRow from './ResultRow'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AccordionOrigin from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
// themes
import { colors } from 'themes/index'
// constants
import { Forms } from 'constants/index'
import { safeGetOr } from 'utils/fp'

const Accordion = styled(AccordionOrigin)`
  && {
    border-radius: 4px;
    box-shadow:
      0 1px 3px 0 rgba(0, 0, 0, 0.2),
      0 1px 1px 0 rgba(0, 0, 0, 0.14),
      0 0 0 0 rgba(0, 0, 0, 0.12);
    margin: 8px 0;

    &:before {
      display: none;
    }
  }
`

const resultFormSelector = formValueSelector(Forms.KPI_RESULT_TARGETS_FORM)

const DefaultTarget = ({ targetForm }) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const { defaultResults, differentResults, formMeta } = useSelector((state) => ({
    defaultResults: resultFormSelector(state, 'defaultResults'),
    differentResults: resultFormSelector(state, 'differentResults'),
    formMeta: getFormMeta(Forms.KPI_RESULT_TARGETS_FORM)(state)
  }))

  const dispatch = useDispatch()
  const changeField = (field, value) => dispatch(change(Forms.KPI_RESULT_TARGETS_FORM, field, value))

  const touchedIndexes = React.useMemo(() => {
    const resultsMeta = safeGetOr([], 'differentResults')(formMeta)

    const indexes = []

    resultsMeta.forEach((period, index) => {
      const results = safeGetOr([], 'results')(period)

      const isAnyFieldTouched = results.some(flow(values, map(values), flatten, some(Boolean)))

      if (isAnyFieldTouched) {
        indexes.push(index)
      }
    })

    return indexes
  }, [formMeta])

  const handleChangeField = () => {
    const totalNumber = differentResults.length

    batch(() => {
      times((index) => {
        if (!touchedIndexes.includes(index)) {
          changeField(`differentResults[${index}].results`, defaultResults)
        }
      }, totalNumber)
    })
  }

  return (
    <Accordion expanded={isOpen} onChange={(_, expanded) => setIsOpen(expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon htmlColor={colors.blue500} />}>
        <Typography style={{ color: colors.blue500, fontSize: 16, fontWeight: isOpen ? 600 : 400 }}>
          Default result targets
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'flex', flexDirection: 'column', padding: '8px 24px' }}>
        <FieldArray name={'defaultResults'} component={ResultRow} targetForm={targetForm} />
        <Button type={'button'} layout={BUTTON_LAYOUTS.PURPLE} shadowLess onClick={handleChangeField}>
          Apply
        </Button>
      </AccordionDetails>
    </Accordion>
  )
}

export default DefaultTarget
